import { legacy_createStore as createStore, applyMiddleware, compose, combineReducers } from 'redux'
import { thunk } from 'redux-thunk'
import { createLogger } from 'redux-logger'
import promiseMiddleware from 'redux-promise-middleware'

import { TypedUseSelectorHook, UseDispatch, useDispatch, useSelector } from 'react-redux'
import { asyncUserBootstrapMiddleware } from './userMiddleware'
import { save } from 'redux-localstorage-simple'
import { mergeDeepRight, values } from 'ramda'
import { createReducers, createSetStore, createUseStore, getStore } from '@cnd/redash/funstore'
import { configuration, Configuration } from './storeConfiguration'
import initialState, { InitialState } from './initialState'
import { firebaseListenerMiddleware } from './useFirebaseConnect'
import { backgroundActionsMiddleware } from './backgroundActionsMiddleware'
import { isBefore } from 'date-fns'
import { getDerivedBusinessHours } from './useBusinessHours'
import { difference } from '@cnd/redash'
import { calculateDeliveryTimeRange } from '@cnd/common/functions/delivery'

export const useStore = createUseStore<TypedUseSelectorHook<InitialState>, InitialState>(useSelector)
export const useSetStore = createSetStore<Configuration, UseDispatch>(useDispatch)
export const get = getStore<InitialState>()

const middleware = [
  thunk,
  promiseMiddleware,
  asyncUserBootstrapMiddleware,
  firebaseListenerMiddleware([]),
  backgroundActionsMiddleware([{ actionType: 'SYNC_BUSINESS_HOURS_WITH_USER_TIME', interval: 25000 }]),
]

const logger = createLogger({ diff: true })
middleware.push(logger)
middleware.push(save({ namespace: 'root' }))

const appReducer = combineReducers(createReducers(configuration))

const rootReducer = (state: InitialState, action) => {
  if (action.type === 'SET_PERSISTED_STATE') {
    return appReducer(mergeDeepRight(initialState, action.payload), action)
  }

  if (action.type === 'SYNC_BUSINESS_HOURS_WITH_USER_TIME') {
    const closestToUser = state.locations?.closestToUser
    const searchPreOrder = state.locations?.searchPreOrder
    let locationsState = state.locations

    if (closestToUser) {
      const newClosestToUser = getDerivedBusinessHours(closestToUser)
      const newLocationsState = {
        ...locationsState,
        closestToUser: newClosestToUser,
        searchPreOrder: newClosestToUser.isShopClosed ? true : searchPreOrder,
      }

      const diff = difference(newLocationsState, state)
      if (values(diff).length !== 0) locationsState = newLocationsState
    }

    // if basket is set to old now for a time that is no longer available, set the basket to now now
    // if basket is pre booked for a time that is no longer available, clear the basket and set to now now
    // if basket has never been set, just return with updated business hours

    const orderState = state?.user?.order

    const expectedDeliveryTimeFrom = orderState?.expectedDeliveryTimeFrom
    const isShopClosed = locationsState?.closestToUser?.isShopClosed

    if (
      isBefore(new Date(String(expectedDeliveryTimeFrom)), new Date()) &&
      closestToUser &&
      !isShopClosed &&
      orderState.deliveryLocation
    ) {
      // set to delivery now
      return appReducer(
        mergeDeepRight(state, {
          locations: locationsState,
          user: {
            order: {
              whenAndWhereSet: true,
              isCollection: false,
              isPreOrder: false,
              collectionLocation: null,
              prepLocation: closestToUser,
              ...calculateDeliveryTimeRange(orderState?.items),
            },
          },
        }),
        action
      )
    }

    if (isBefore(new Date(String(expectedDeliveryTimeFrom)), new Date()) && closestToUser && isShopClosed) {
      return appReducer(
        mergeDeepRight(state, {
          locations: state.locations?.choosingLocation
            ? locationsState
            : {
                ...locationsState,
                searchExpectedDeliveryTimeFrom: null,
                searchExpectedDeliveryTimeTo: null,
              },
          user: {
            order: {
              whenAndWhereSet: false,
              isCollection: null,
              isPreOrder: null,
              collectionLocation: null,
              prepLocation: closestToUser,
              expectedDeliveryTimeFrom: null,
              expectedDeliveryTimeTo: null,
            },
          },
        }),
        action
      )
    }

    return appReducer(state, action)
  }

  return appReducer(state, action)
}

const store = createStore(rootReducer, initialState, compose(applyMiddleware(...middleware)))

export default store
